import { ref } from 'vue';

import { RpcApiService } from '@/api/client/services/SSEApiService';
import { fileExtensions } from '@/constants/file';
import { sleep } from '@/shared/helpers';
import { rpcStorage } from '@/shared/lib/storage/rpc-storage';

import { processManager } from './processManager';
import {
  FileItem,
  ICreateRpcClient,
  RpcClientFactory,
  TaskCreate,
  TypeFiles,
} from './types';

// TODO Переместить в src/shared/...
export interface IusePollWithRetryOptions {
  retries?: number;
  delay?: number;
  silent?: boolean;
  onFinalFail?: () => void;
}
async function usePollWithRetry<T>(
  fn: () => Promise<T | false>,
  {
    retries = 2,
    delay = 5000,
    silent = true,
    onFinalFail,
  }: IusePollWithRetryOptions = {}
): Promise<T | false> {
  let attempt = 0;

  while (attempt <= retries) {
    try {
      const result: any = await fn();

      if (result?.status) return result;

      attempt++;
      if (attempt > retries) break;
    } catch (e) {
      attempt++;
      if (!silent) console.error(e);
      if (attempt > retries) break;
    }

    await sleep(delay, true);
  }

  onFinalFail?.();
  return false;
}
//

export const createRpcClient = ({
  apiService,
  notifyError,
}: RpcClientFactory): ICreateRpcClient => {
  const rpcApiService = new RpcApiService();

  const urlMap = ref<TypeFiles>();
  const taskGuid = ref<string>();

  const listGet = (params?: any) => {
    return apiService.listGet(params);
  };
  const getRouter = (id: string, name: string, params?: any) => {
    if (!id) return;
    rpcStorage.updateMeta(id, { name, params });
  };

  const taskCreate = async (
    cfg: TaskCreate,
    fileName: string,
    format: string | null = null,
    nameAction = 'формирование'
  ) => {
    if (cfg?._response?.status === 304) {
      notifyError(`Вы уже делали запрос на ${nameAction}`);
      return false;
    }
    if (!cfg?.status) return false;
    const { app_task_id, celery_task_id, app_dims } = cfg.response;
    const stopFlag = { stopped: false };
    processManager.register(app_task_id, () => {
      stopFlag.stopped = true;
    });

    while (!stopFlag.stopped) {
      await sleep(5000, true);
      const result = (await usePollWithRetry(
        () => apiService.check(app_task_id, celery_task_id),
        {
          silent: true,
          onFinalFail: () => notifyError(result.errors[0]?.message),
        }
      )) as any;
      if (!result?.status) return false;

      const { meta, data } = result.response;

      const task: FileItem = {
        ...meta,
        app_hash_md5: meta.app_hash_md5,
        file_name: fileName,
        format,
        type: app_dims[2],
        files: data?.response?.result_download_url,
        back_queue: meta.back_queue,
        percent: meta.percent,
      };
      rpcStorage.upsert(task);
      switch (meta.state) {
        case 'SUCCESS':
          if (meta.route?.startsWith('print_')) {
            urlMap.value = data?.response?.result_download_url;
          } else {
            taskGuid.value = data?.req_cache_guid;
          }
          return true;
        case 'FAILURE':
          notifyError(data?.error?.details || `Ошибка при "${nameAction}"`);
          return false;
        case 'STALED':
          notifyError(
            'Произошла ошибка, задача отправлена на повторное выполнение'
          );
          return 'STALED';
        case 'ttl':
        case 'try_term':
          return false;
      }
    }
  };

  const createFileDownloadTask = (fileName: string, totalFiles: number) => {
    const app_task_id = crypto.randomUUID();
    const stopFlag = { stopped: false };
    processManager.register(app_task_id, () => {
      stopFlag.stopped = true;
    });
    const task: FileItem = {
      app_hash_md5: app_task_id,
      state: 'PENDING',
      file_name: fileName,
      format: `${fileExtensions.pdf}+${fileExtensions.xlsx}`,
      total_files: totalFiles,
      downloaded_files: 0,
      files: '',
      back_queue: 'download files',
    };
    rpcStorage.upsert(task);
    let successCount = 0;
    let totalCount = 0;
    const updateProgress = (success: boolean) => {
      if (stopFlag.stopped) return;
      totalCount++;
      if (success) successCount++;
      rpcStorage.upsert({
        ...task,
        downloaded_files: successCount,
        state: totalCount >= totalFiles ? 'SUCCESS' : 'PENDING',
      });
    };
    return {
      app_task_id,
      updateProgress,
    };
  };

  const stopTask = (id: string) => processManager.remove(id);
  const stopAll = () => processManager.clear();

  const removeTask = async (task: FileItem) => {
    if (!task.total_files) {
      await rpcApiService.delete(task.app_hash_md5);
    }

    stopTask(task.app_hash_md5);
    rpcStorage.remove(task.app_hash_md5);
  };

  return {
    taskCreate,
    getRouter,
    getUrlMap: () => urlMap.value,
    getTaskGuid: () => taskGuid.value,
    listGet,
    createFileDownloadTask,

    stopTask,
    stopAll,

    removeTask,
  };
};
